import React, { FC, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import {
  MenuTileCarousel,
  MenuTileGridTwoRowsCarousel,
} from '@ldp-web/component-library/ui/organisms/menu-tile-carousel';
import { IComponentCommonProps, IHeaderGroupComponentProps, TImageType, TSDMCta } from '@ldp/common-types';
import { useVariantIDMap } from '../../hooks';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { useSDMRouterContext } from '@ldp/sdm-contexts';
import { useRedirectLink } from '../../utils';
import { HeaderGroupComponent } from '../header-group';
import { WrapperComponent } from '../wrapper';

/** Menu Tile Carousel props */
export interface IMenuTileCarouselComponentProps extends IComponentCommonProps {
  /** Title for the carousel */
  headline: string;
  /** CTA Link for the component */
  callToAction?: TSDMCta;
  /** Menu tile data to show tiles */
  menuTiles: MenuTileType[];
  /** Variant ID */
  variantId?: string;
  /** Menu Tile Carousel image size */
  size?: 'default' | 'large' | 'wide' | 'largeWide';
  /** Menu Tile Carousel image border */
  isBorder?: boolean;
  /** header group details */
  headerGroup?: IHeaderGroupComponentProps;
  /** content id of component */
  contentId?: string;
  /** determines if the component should be wrapped in wrapper component */
  noOuterWrapper?: boolean;
  /** no wrapper component styles */
  noOuterWrapperCSS?: React.CSSProperties;
  /** id of component */
  id?: string;
}

/** Menu Tile Type with CTA and Image */
export type MenuTileType = {
  callToAction: TSDMCta;
  image: TImageType;
};

const variantIdsForTwoRows = [
  'TWO_ROW_LARGE_IMAGE',
  'TWO_ROWS_WIDE_IMAGE',
  'TWO_ROWS_STROKE_WIDE_IMAGE',
  'TWO_ROWS_STROKE_IMAGE',
];

/** Renders Menu Tile Carousel Component */
export const MenuTileCarouselComponent: FC<IMenuTileCarouselComponentProps> = (props) => {
  const {
    headline,
    menuTiles,
    headerGroup,
    callToAction,
    variantId = 'DEFAULT',
    styling,
    contentId,
    id,
    noOuterWrapper = false,
    noOuterWrapperCSS = {},
  } = props;
  const { size, isBorder } = useVariantIDMap(variantId);
  const isTwoRowGridCarousel = variantIdsForTwoRows.includes(variantId);
  const i18n = useSimpleTranslation();
  const skipLabel = i18n('skip');
  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);

  // Ensure each menuTile has a unique identifier for React keys
  const menuTilesWithIds = useMemo(() => {
    return menuTiles.map((tile, index) => ({
      ...tile,
      id: (tile as any).id || tile.callToAction?.url || `menu-tile-${contentId || id || 'default'}-${index}`,
    }));
  }, [menuTiles, contentId, id]);

  const headerGroupProps: IHeaderGroupComponentProps = headerGroup ?? {
    headline: {
      text: headline,
      variantId: 'MEDIUM_03',
      callToAction: callToAction,
      clickHandler: redirectToPage,
    },
  };

  const mainContent = (
    <>
      <HeaderGroupComponent {...headerGroupProps} noWrapper />
      {isTwoRowGridCarousel ? (
        <MenuTileGridTwoRowsCarousel
          menuTileList={menuTilesWithIds}
          size={size}
          isBorder={isBorder}
          skipLinkLabel={skipLabel}
          callToActionHandler={redirectToPage}
        />
      ) : (
        <MenuTileCarousel
          menuTileList={menuTilesWithIds}
          size={size}
          isBorder={isBorder}
          skipLinkLabel={skipLabel}
          callToActionHandler={redirectToPage}
        />
      )}
    </>
  );

  if (noOuterWrapper) {
    return <Box style={noOuterWrapperCSS}>{mainContent}</Box>;
  }

  return (
    <WrapperComponent isExtraLargeSpacing id={id} styling={styling}>
      {mainContent}
    </WrapperComponent>
  );
};

export default MenuTileCarouselComponent;
